import { useQuery } from '@tanstack/react-query';
import { schedule } from '@cwos/api-client';
import type { schedule as SCH } from '@cwos/api-client';
import { Pill, SuccessEmpty } from '@cwos/ui-admin';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import {
  CAPACITY_READING_TEXT,
  DATED_STANDING_READING,
  availabilityStanding,
  capacityReading,
  dateWindowReading,
  dayName,
  hoursAndMinutes,
  localDateIn,
} from '../schedule/coverageLogic.js';

/**
 * WHEN THIS PERSON SAYS THEY CAN WORK, AND WHAT IS LEFT OF IT.
 *
 * `GET /availability/workers/:id` and `.../capacity` have existed with no
 * caller. A scheduler answered "is Maria free Thursday" by asking Maria.
 *
 * ---------------------------------------------------------------------------
 * AN AVAILABILITY BLOCK IS A DATED FACT AND THE READ RETURNS ALL OF THEM
 * ---------------------------------------------------------------------------
 * `worker_availability` rows are ENDED on a date, never deleted, so that "was
 * this person available then" stays answerable. The list read returns every row
 * — including one that stopped applying in March. Rendering all of them under a
 * heading that says "available" tells a scheduler somebody works Tuesdays when
 * they have not since the spring, which is precisely the kind of confident
 * wrong answer a staffing decision then gets made on.
 *
 * So the blocks are split by `availabilityStanding` against TODAY IN THE
 * ORGANIZATION'S ZONE — carried per row as `timezone`, because "09:00" is not a
 * fact until somebody says where — and the ones that no longer apply are kept
 * on the page, under their own heading, rather than filtered into silence.
 *
 * ---------------------------------------------------------------------------
 * THREE ZEROES, AND ONLY ONE OF THEM IS A ZERO
 * ---------------------------------------------------------------------------
 *   "no availability declared"  — nobody asked them; nobody knows.
 *   "fully booked"              — declared, and every hour is committed.
 *   "we could not read it"      — an error, or a permission.
 *
 * The server already separates the first two: `declaredNothing` is its own
 * field precisely because `remainingMinutes === 0` is ambiguous. This screen
 * keeps them apart, and `deductionsWithheld` keeps a fourth apart from all of
 * them — a caller with a scheduling key but no timeoff reach is told the
 * totals and NOT told what consumed them, and that is a withholding, not a
 * zero.
 */
export function WorkerAvailability({
  workerId,
  /** Injected so the dated split is testable without a fake clock. */
  now = new Date(),
}: {
  workerId: string;
  now?: Date;
}) {
  const blocks = useQuery({
    queryKey: ['availability', 'worker', workerId],
    queryFn: () => schedule.workerAvailability(workerId),
  });

  const capacity = useQuery({
    queryKey: ['availability', 'capacity', workerId],
    queryFn: () => schedule.workerCapacity(workerId),
  });

  const rows = blocks.data ?? [];
  const phase = listPhase(blocks, rows.length);

  // The zone comes off the ROWS, which carry the org's zone per row, and falls
  // back to the capacity window's zone. Never the device's: a reader three
  // zones away splits "in force today" on the wrong day otherwise.
  const timezone = rows[0]?.timezone ?? capacity.data?.window.timezone ?? 'UTC';
  const today = localDateIn(timezone, now);

  const inForce: SCH.AvailabilityBlock[] = [];
  const past: SCH.AvailabilityBlock[] = [];
  const future: SCH.AvailabilityBlock[] = [];
  for (const b of rows) {
    const standing = availabilityStanding(b, today);
    if (standing === 'in_force') inForce.push(b);
    else if (standing === 'not_yet') future.push(b);
    else past.push(b);
  }

  return (
    <section className="rn-card">
      <h2 className="rn-card-title">Declared availability</h2>

      <ListState
        phase={phase}
        surface="table"
        rows={3}
        failedMessage="We couldn’t read this person’s availability. Do not read this as “they are never free”."
        refused={
          <p className="rn-muted">
            This person’s declared availability is not shown to you. That is an answer, not an empty
            week — do not read it as “they have declared nothing”.
          </p>
        }
        empty={
          <SuccessEmpty
            heading="Nothing declared"
            message="This person has not said when they can work. That is different from being unavailable — nobody has been asked."
          />
        }
      >
        <>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {inForce.map((b) => (
              <li key={b.id} className="drawer-row">
                <span>
                  {dayName(b.dayOfWeek)} · {b.startsAtLocal}–{b.endsAtLocal}{' '}
                  <span className="rn-muted">({b.timezone})</span>
                </span>
                <span className="rn-muted">
                  {dateWindowReading(b.effectiveFrom, b.effectiveTo) ?? ''}
                </span>
              </li>
            ))}
          </ul>

          {inForce.length === 0 ? (
            <p className="rn-muted">
              Nothing this person declared is in force today. Every block below either started later
              or has ended — this is not the same as never having declared anything.
            </p>
          ) : null}

          {future.length > 0 ? (
            <>
              <h3 className="drawer-label">Starts later</h3>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {future.map((b) => (
                  <li key={b.id} className="drawer-row">
                    <span>
                      {dayName(b.dayOfWeek)} · {b.startsAtLocal}–{b.endsAtLocal}
                    </span>
                    <Pill variant="unsure" dot>
                      {DATED_STANDING_READING.not_yet}
                    </Pill>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {past.length > 0 ? (
            <>
              <h3 className="drawer-label">No longer in force</h3>
              <p className="rn-muted" style={{ marginTop: 0 }}>
                Kept because “was this person available then” has to stay answerable. Do not staff
                against these.
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {past.map((b) => (
                  <li key={b.id} className="drawer-row">
                    <span className="rn-muted">
                      {dayName(b.dayOfWeek)} · {b.startsAtLocal}–{b.endsAtLocal} ·{' '}
                      {dateWindowReading(b.effectiveFrom, b.effectiveTo)}
                    </span>
                    <Pill variant="plain" dot>
                      {DATED_STANDING_READING.ended}
                    </Pill>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      </ListState>

      <h3 className="drawer-label">This week</h3>
      {capacity.isError ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          We couldn’t work out how much of this week is left. Do not read that as “no hours left”.
        </p>
      ) : capacity.data === undefined ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          Working out remaining capacity…
        </p>
      ) : (
        <CapacityLines capacity={capacity.data} />
      )}
    </section>
  );
}

function CapacityLines({ capacity }: { capacity: SCH.CapacityView }) {
  const reading = capacityReading(capacity);
  return (
    <div className="drawer-rows">
      <p className="rn-muted" style={{ margin: 0 }}>
        {CAPACITY_READING_TEXT[reading]}
      </p>
      {reading === 'undeclared' ? null : (
        <>
          <div className="drawer-row">
            <span className="rn-muted">Declared</span>
            <span>{hoursAndMinutes(capacity.declaredMinutes)}</span>
          </div>
          <div className="drawer-row">
            <span className="rn-muted">Committed</span>
            <span>{hoursAndMinutes(capacity.committedMinutes)}</span>
          </div>
          <div className="drawer-row">
            <span className="rn-muted">Remaining</span>
            <span>{hoursAndMinutes(capacity.remainingMinutes)}</span>
          </div>
        </>
      )}
      {/*
        WITHHELD, NOT ZERO, AND READ BEFORE THE NULL.

        The per-source breakdown names `time_off` minutes, which is a timeoff
        fact and not a scheduling one, so the server withholds the whole GROUP
        behind one flag rather than per field — a zero deduction is legitimate
        on a full row, and a per-field flag would report "no value" while the
        caller was in fact being told nothing. Checking `deductions === null`
        first would print "none" for "not yours".
      */}
      {capacity.deductionsWithheld ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          What consumed these hours is not shown to you — some of it is time off, which is not a
          scheduling fact. The totals above are complete; the breakdown is withheld, not empty.
        </p>
      ) : capacity.deductions === null ? null : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {Object.entries(capacity.deductions)
            .filter(([, minutes]) => minutes > 0)
            .map(([source, minutes]) => (
              <li key={source} className="drawer-row">
                <span className="rn-muted">{source.replace(/_/g, ' ')}</span>
                <span>{hoursAndMinutes(minutes)}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
